import { TDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import useCost from 'hooks/token-town/useCost';
import { useMemo, useState } from 'react';
import { IEntryLine } from 'types/Draws';
import {
  lineContainsDuplicates,
  shouldShowErrorForNumberChoice,
} from 'utils/LottoEntryValidator';
import { INSUFFICIENT_TOKENS_MESSAGE } from 'utils/token-town/InsufficientTokenMessage';

export function useWeeklyDrawPurchaseState({
  entryCost,
  playerTokenCount,
  numberRanges,
  entries,
}: {
  entryCost: number;
  playerTokenCount: number;
  numberRanges: TDrawTypeNumberSetup;
  entries: IEntryLine[];
}) {
  const costOfEntries = useCost(entries.length, entryCost);
  const [inputError, setInputError] = useState(false);

  const hasEnoughTokens = useMemo(() => {
    return playerTokenCount >= costOfEntries;
  }, [playerTokenCount, costOfEntries]);

  const hasInvalidNumbers = useMemo(() => {
    return entries.some((entry) => {
      if (lineContainsDuplicates(entry.main)) return true;

      if (
        entry.main.some(
          (num, idx) =>
            num === null ||
            isNaN(num) ||
            shouldShowErrorForNumberChoice(num, numberRanges.main[idx])
        )
      ) {
        return true;
      }

      if (
        entry.bonus.some(
          (num, idx) =>
            num === null ||
            isNaN(num) ||
            shouldShowErrorForNumberChoice(num, numberRanges.bonus[idx])
        )
      ) {
        return true;
      }
    });
  }, [entries, numberRanges]);

  const ctaButtonDisabled = useMemo(() => {
    return !hasEnoughTokens || hasInvalidNumbers || inputError;
  }, [hasEnoughTokens, hasInvalidNumbers, inputError]);

  const ctaText = useMemo(() => {
    if (!hasEnoughTokens) return INSUFFICIENT_TOKENS_MESSAGE;
    if (hasInvalidNumbers) return 'ENTER DRAW';
    return `ENTER DRAW FOR ${costOfEntries} TOKEN${costOfEntries === 1 ? '' : 'S'}`;
  }, [hasEnoughTokens, hasInvalidNumbers, costOfEntries]);

  return {
    hasEnoughTokens,
    costOfEntries,
    ctaText,
    ctaButtonDisabled,
    setInputError,
  };
}
